/**
 * Comunicação: canais e mensagens (SPEC-20260808-1500).
 *
 * Aqui moram o formato das entidades e as regras que NÃO podem divergir entre
 * a API e as telas — quem pode apagar, quem pode moderar, como as reações se
 * agrupam. Nada de HTTP, nada de banco.
 */

/** Tipos de canal. `pessoa` é a conversa direta entre dois. */
export const TIPOS_DE_CANAL = ['time', 'produto', 'empresa', 'projeto', 'pessoa'] as const
export type TipoDeCanal = (typeof TIPOS_DE_CANAL)[number]

/** Papel dentro de UM canal. Quem modera um grupo não modera os outros. */
export const PAPEIS_NO_CANAL = ['member', 'moderator'] as const
export type PapelNoCanal = (typeof PAPEIS_NO_CANAL)[number]

/**
 * O que dá para anexar.
 *
 * Os quatro primeiros carregam arquivo; os três últimos são estruturados e
 * viajam em `payload`.
 */
export const TIPOS_DE_ANEXO = [
  'documento',
  'imagem',
  'video',
  'audio',
  'contato',
  'enquete',
  'evento',
  /** GIF do Giphy: só a URL pública viaja em `payload`, nada é baixado. */
  'gif',
] as const
export type TipoDeAnexo = (typeof TIPOS_DE_ANEXO)[number]

/** Um GIF da busca, já no formato que a tela usa. */
export type GifDoGiphy = {
  id: string
  titulo: string
  /** Versão para a conversa (largura fixa, ~200px). */
  url: string
  /** Versão leve para a grade do seletor. */
  previa: string
  largura: number
  altura: number
}

/** Como cada tipo aparece no menu do `+`, na ordem em que é desenhado. */
export const MENU_DE_ANEXOS: readonly {
  tipo: TipoDeAnexo
  label: string
  icone: string
  /** Abre seletor de arquivo com este filtro; vazio = não é arquivo. */
  accept: string
}[] = [
  { tipo: 'documento', label: 'Documento', icone: 'fas fa-file-alt', accept: '*/*' },
  { tipo: 'imagem', label: 'Fotos e vídeos', icone: 'fas fa-image', accept: 'image/*,video/*' },
  { tipo: 'video', label: 'Câmera', icone: 'fas fa-camera', accept: 'image/*;capture=camera' },
  { tipo: 'audio', label: 'Áudio', icone: 'fas fa-headphones', accept: 'audio/*' },
  { tipo: 'contato', label: 'Contato', icone: 'fas fa-user', accept: '' },
  { tipo: 'enquete', label: 'Enquete', icone: 'fas fa-list-ul', accept: '' },
  { tipo: 'evento', label: 'Evento', icone: 'fas fa-calendar-day', accept: '' },
] satisfies readonly { tipo: Exclude<TipoDeAnexo, 'gif'>; label: string; icone: string; accept: string }[]

/**
 * A barra rápida de reação.
 *
 * Seis fixos porque cobrem quase tudo com um toque; o `+` abre o teclado de
 * emoji do sistema para o resto (decisão do usuário, 2026-09-12).
 */
export const EMOJIS_RAPIDOS = ['👍', '❤️', '😂', '😮', '😢', '🙏'] as const

export type AutorDaMensagem = {
  memberId: string
  name: string
  avatarUrl: string | null
  hasAvatar: boolean
  avatarVersion: string | null
}

export type AnexoDaMensagem = {
  id: string
  tipo: TipoDeAnexo
  fileName: string | null
  mimeType: string | null
  sizeBytes: number | null
  /** Contato, enquete e evento — já desserializado. */
  payload: Record<string, unknown> | null
  /** Só no anexo `enquete`: a apuração do jeito que a tela desenha. */
  enquete?: EnqueteApurada | null
}

/* ------------------------------------------------- silenciar --- */

/** As opções de silêncio, na ordem do menu. */
export const OPCOES_DE_SILENCIO = [
  { id: '8h', rotulo: '8 horas' },
  { id: '1semana', rotulo: '1 semana' },
  { id: 'sempre', rotulo: 'Sempre' },
] as const
export type DuracaoDoSilencio = (typeof OPCOES_DE_SILENCIO)[number]['id']

/** "Sempre" = data muito distante; o `infinity` do banco não cabe num Date. */
export const SILENCIO_PARA_SEMPRE = '9999-12-31T23:59:59.000Z'

/** Até quando fica silenciada, a partir de agora. */
export function silenciarAte(duracao: DuracaoDoSilencio, agora: Date = new Date()): Date {
  if (duracao === 'sempre') return new Date(SILENCIO_PARA_SEMPRE)
  const horas = duracao === '8h' ? 8 : 7 * 24
  return new Date(agora.getTime() + horas * 60 * 60 * 1000)
}

/** Está silenciada AGORA? (8 horas e 1 semana vencem sozinhas.) */
export function estaSilenciada(ate: string | Date | null | undefined, agora: Date = new Date()): boolean {
  return ate != null && new Date(ate).getTime() > agora.getTime()
}

/** Texto do estado para a tela: "Silenciada até 15/09, 09:30" ou "Silenciada". */
export function textoDoSilencio(ate: string | null): string {
  if (!estaSilenciada(ate)) return 'Notificações ativadas'
  if (new Date(ate!).getUTCFullYear() >= 9999) return 'Silenciada para sempre'
  const data = new Date(ate!)
  return `Silenciada até ${data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}, ${data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
}

/* ------------------------------------------ mídia, links e docs --- */

/** Um arquivo compartilhado (foto, vídeo, GIF, documento, áudio). */
export type ArquivoDoAcervo = {
  id: string
  messageId: string
  channelId: string
  /** Nome da conversa — aparece no "Ver mídias de todas as conversas". */
  canal: string
  tipo: TipoDeAnexo
  fileName: string | null
  mimeType: string | null
  sizeBytes: number | null
  /** GIF: a URL pública vem aqui (não há arquivo nosso). */
  urlExterna: string | null
  enviadaEm: string
}

/** Um link achado no texto de uma mensagem. */
export type LinkDoAcervo = {
  /** messageId + posição: a mesma mensagem pode ter vários links. */
  id: string
  messageId: string
  channelId: string
  canal: string
  url: string
  dominio: string
  /** O texto em volta, para lembrar do que se tratava. */
  trecho: string
  enviadaEm: string
}

export type AcervoDaConversa = {
  midia: ArquivoDoAcervo[]
  documentos: ArquivoDoAcervo[]
  links: LinkDoAcervo[]
}

/** Foto, vídeo e GIF vão para "Mídia"; documento e áudio para "Documentos". */
export const TIPOS_DE_MIDIA: readonly TipoDeAnexo[] = ['imagem', 'video', 'gif']
export const TIPOS_DE_DOCUMENTO: readonly TipoDeAnexo[] = ['documento', 'audio']

/** Links http(s) de um texto, sem pontuação final colada ("veja x.com/a."). */
export function extrairLinks(texto: string): string[] {
  const achados = texto.match(/https?:\/\/[^\s<>"']+/gi) ?? []
  return [...new Set(achados.map((u) => u.replace(/[.,;:!?)\]}]+$/, '')))]
}

/** "https://www.exemplo.com.br/x" -> "exemplo.com.br". */
export function dominioDoLink(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

/**
 * Rótulo do grupo de meses no acervo: "Neste mês", "Mês passado" e, antes
 * disso, "Agosto de 2026" — como no mensageiro de referência.
 */
export function rotuloDoMes(iso: string, hoje: Date = new Date()): string {
  const data = new Date(iso)
  const meses = (hoje.getFullYear() - data.getFullYear()) * 12 + (hoje.getMonth() - data.getMonth())
  if (meses <= 0) return 'Neste mês'
  if (meses === 1) return 'Mês passado'
  const nome = data.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
  return nome.charAt(0).toUpperCase() + nome.slice(1)
}

/* ------------------------------------------------------- enquete --- */

/** Uma opção já apurada: quantos votos, se eu votei nela e quem votou. */
export type OpcaoApurada = {
  indice: number
  texto: string
  total: number
  euVotei: boolean
  quem: readonly string[]
}

export type EnqueteApurada = {
  pergunta: string
  /** Várias respostas por pessoa, ou só uma. */
  multipla: boolean
  opcoes: readonly OpcaoApurada[]
  /** Pessoas diferentes que votaram (não a soma dos votos). */
  votantes: number
}

/** Limites da enquete — os mesmos no formulário e na API. */
export const ENQUETE_MIN_OPCOES = 2
export const ENQUETE_MAX_OPCOES = 12

/** Lê o payload gravado sem confiar no formato (veio de JSON solto). */
export function lerEnquete(payload: Record<string, unknown> | null): {
  pergunta: string
  opcoes: string[]
  multipla: boolean
} {
  const p = payload ?? {}
  return {
    pergunta: typeof p.pergunta === 'string' ? p.pergunta : 'Enquete',
    opcoes: Array.isArray(p.opcoes) ? p.opcoes.filter((o): o is string => typeof o === 'string') : [],
    // Enquete antiga não tinha o campo: vale "uma resposta".
    multipla: p.multipla === true,
  }
}

/** Apura os votos: contagem por opção, meu voto e o total de pessoas. */
export function apurarEnquete(
  payload: Record<string, unknown> | null,
  votos: readonly { opcao: number; memberId: string; nome: string }[],
  euMemberId: string,
): EnqueteApurada {
  const { pergunta, opcoes, multipla } = lerEnquete(payload)
  return {
    pergunta,
    multipla,
    opcoes: opcoes.map((texto, indice) => {
      const daOpcao = votos.filter((v) => v.opcao === indice)
      return {
        indice,
        texto,
        total: daOpcao.length,
        euVotei: daOpcao.some((v) => v.memberId === euMemberId),
        quem: daOpcao.map((v) => v.nome),
      }
    }),
    votantes: new Set(votos.filter((v) => v.opcao < opcoes.length).map((v) => v.memberId)).size,
  }
}

/**
 * Meus votos depois de tocar numa opção.
 *
 * Tocar na opção já marcada DESMARCA (nas duas modalidades). Em "uma resposta",
 * marcar outra troca o voto; em "várias", soma.
 */
export function votosDepoisDeTocar(atuais: readonly number[], tocada: number, multipla: boolean): number[] {
  if (atuais.includes(tocada)) return atuais.filter((o) => o !== tocada)
  return multipla ? [...atuais, tocada].sort((a, b) => a - b) : [tocada]
}

/**
 * A enquete já apurada depois do MEU toque, sem ir ao servidor — é o que faz
 * o voto aparecer na hora (antes a tela esperava a enquete voltar, 3–4 s).
 * O servidor confirma logo depois e a apuração dele substitui esta.
 */
export function aplicarMeuVoto(enquete: EnqueteApurada, opcao: number, meuNome: string): EnqueteApurada {
  const antes = enquete.opcoes.filter((o) => o.euVotei).map((o) => o.indice)
  const depois = votosDepoisDeTocar(antes, opcao, enquete.multipla)
  const votavaAntes = antes.length > 0
  const votaDepois = depois.length > 0
  return {
    ...enquete,
    votantes: enquete.votantes + (votaDepois ? 1 : 0) - (votavaAntes ? 1 : 0),
    opcoes: enquete.opcoes.map((o) => {
      const agora = depois.includes(o.indice)
      if (agora === o.euVotei) return o
      return {
        ...o,
        euVotei: agora,
        total: o.total + (agora ? 1 : -1),
        quem: agora ? [...o.quem, meuNome] : o.quem.filter((n, i) => !(n === meuNome && i === o.quem.indexOf(meuNome))),
      }
    }),
  }
}

/** Percentual da barra: fração dos votantes que escolheram a opção. */
export function percentualDaOpcao(total: number, votantes: number): number {
  return votantes === 0 ? 0 : Math.round((total / votantes) * 100)
}

/** Reações já agrupadas por emoji: é assim que a tela desenha. */
export type ReacaoAgrupada = {
  emoji: string
  total: number
  /** Eu estou entre quem reagiu? Decide o destaque do chip. */
  euReagi: boolean
  /** Nomes de quem reagiu, para a dica ao passar o mouse. */
  quem: readonly string[]
}

/** A citação mostrada em cima de uma resposta. */
export type CitacaoDaMensagem = {
  id: string
  autor: string | null
  trecho: string
  /** A citada foi apagada: a resposta continua, a citação vira aviso. */
  apagada: boolean
}

export type MensagemDoCanal = {
  id: string
  channelId: string
  /** Null em mensagem de sistema ("Fulano entrou no canal"). */
  autor: AutorDaMensagem | null
  corpo: string
  /** ISO. A tela formata; guardar formatado impede reordenar e reagrupar. */
  enviadaEm: string
  editadaEm: string | null
  apagadaEm: string | null
  respondendo: CitacaoDaMensagem | null
  encaminhada: boolean
  anexos: readonly AnexoDaMensagem[]
  reacoes: readonly ReacaoAgrupada[]
  fixada: boolean
  favorita: boolean
  /** O texto traz "@Meu Nome": o balão ganha destaque para quem foi chamado. */
  mencionaMe: boolean
}

/**
 * A mensagem me menciona?
 *
 * Menção = "@Nome" no texto, o mesmo que o autocompletar insere — é a regra
 * que a contagem de menções não lidas já usa no servidor.
 */
export function mencionaMembro(corpo: string, nome: string | null | undefined): boolean {
  return Boolean(nome) && corpo.includes(`@${nome}`)
}

export type MembroDoCanal = {
  memberId: string
  name: string
  email: string
  avatarUrl: string | null
  hasAvatar: boolean
  avatarVersion: string | null
  papel: PapelNoCanal
  /** Perfil personalizado (2026-09-16): apresentação e banner atrás da foto. */
  bio: string | null
  hasBanner: boolean
}

export type FotoDoCanal =
  | { tipo: 'grupo'; channelId: string; versao: string | null }
  | { tipo: 'membro'; memberId: string; temAvatar: boolean; avatarUrl: string | null; versao: string | null }

/** Uma mensagem achada pela busca da lista. */
export type ResultadoDeBusca = {
  channelId: string
  canal: string
  tipoDoCanal: TipoDeCanal
  messageId: string
  autor: string | null
  /** Eu mandei? A prévia troca o nome por "Você". */
  minha: boolean
  /** Pedaço do texto em volta do termo, para a prévia caber na linha. */
  trecho: string
  enviadaEm: string
}

/** Uma mensagem que me mencionou — a lista do filtro Menções. */
export type MencaoRecebida = ResultadoDeBusca & {
  /** Chegou depois do meu ponto de leitura naquela conversa. */
  naoLida: boolean
}

/**
 * Recorta o texto em volta da primeira ocorrência do termo.
 *
 * A prévia tem uma linha: mandar a mensagem inteira esconderia justamente o
 * trecho que casou quando ele está no fim de um parágrafo.
 */
export function trechoEmVoltaDe(corpo: string, termo: string, folga = 40): string {
  const limpo = corpo.replace(/\s+/g, ' ').trim()
  const onde = limpo.toLowerCase().indexOf(termo.toLowerCase())
  if (onde < 0) return trechoDaMensagem(limpo, folga * 2)
  const inicio = Math.max(0, onde - folga)
  const fim = Math.min(limpo.length, onde + termo.length + folga)
  return `${inicio > 0 ? '…' : ''}${limpo.slice(inicio, fim)}${fim < limpo.length ? '…' : ''}`
}

export type CanalDeChat = {
  id: string
  name: string
  kind: TipoDeCanal
  description: string | null
  icon: string | null
  readOnly: boolean
  memberCount: number
  /** Meu papel neste canal — decide o que o menu da mensagem oferece. */
  meuPapel: PapelNoCanal
  /**
   * De onde vem a foto da linha: a do grupo, ou — em conversa direta — a do
   * outro participante. Null = ícone padrão.
   */
  foto: FotoDoCanal | null
  naoLidas: number
  /** Não lidas que me mencionam (@Meu Nome) — alimenta o filtro Menções. */
  mencoesNaoLidas: number
  /** Silenciei esta conversa até quando (ISO). Null ou vencida = avisa. */
  silenciadaAte: string | null
  /** Menu do botão direito (migration 0107) — tudo aqui é PARA MIM. */
  fixadaEm: string | null
  favoritaEm: string | null
  arquivadaEm: string | null
  /** Marquei como não lida à força — cai sozinho ao abrir a conversa. */
  marcadaNaoLida: boolean
  ultimaMensagem: string | null
  ultimaMensagemEm: string | null
}

/**
 * Quem pode apagar uma mensagem.
 *
 * A regra do usuário: *"se você for dono da mensagem, ou se você tiver
 * moderador daquele chat"*. Mensagem já apagada não se apaga de novo — sem
 * esta guarda o botão continuaria oferecido sobre um buraco.
 */
export function podeApagarMensagem(
  mensagem: Pick<MensagemDoCanal, 'autor' | 'apagadaEm'>,
  euMemberId: string,
  meuPapel: PapelNoCanal,
): boolean {
  if (mensagem.apagadaEm !== null) return false
  if (meuPapel === 'moderator') return true
  return mensagem.autor?.memberId === euMemberId
}

/**
 * Quem pode editar. Só o autor — moderador apaga, não reescreve a fala dos
 * outros.
 */
export function podeEditarMensagem(
  mensagem: Pick<MensagemDoCanal, 'autor' | 'apagadaEm'>,
  euMemberId: string,
): boolean {
  return mensagem.apagadaEm === null && mensagem.autor?.memberId === euMemberId
}

/** Moderação do canal: fixar, mexer na descrição, remover e adicionar gente. */
export function podeModerarCanal(meuPapel: PapelNoCanal): boolean {
  return meuPapel === 'moderator'
}

/**
 * Dá para escrever aqui?
 *
 * Canal somente-leitura é o de avisos: só quem modera publica. O compositor
 * vira aviso de "sem permissão" em vez de sumir — esconder faria parecer que a
 * tela está quebrada.
 */
export function podeEscrever(canal: Pick<CanalDeChat, 'readOnly' | 'meuPapel'>): boolean {
  return !canal.readOnly || canal.meuPapel === 'moderator'
}

/** Trecho da mensagem para a citação e para a prévia da lista de canais. */
export function trechoDaMensagem(corpo: string, limite = 120): string {
  const limpo = corpo.replace(/\s+/g, ' ').trim()
  return limpo.length <= limite ? limpo : `${limpo.slice(0, limite - 1)}…`
}

/**
 * Junta as reações cruas em chips por emoji.
 *
 * DERIVADO, nunca guardado: um contador por emoji na mensagem dessincronizaria
 * na primeira reação perdida. A ordem é por quantidade, e o desempate é a
 * ordem de chegada — sem isso os chips dançam a cada render.
 */
export function agruparReacoes(
  reacoes: readonly { emoji: string; memberId: string; nome: string }[],
  euMemberId: string,
): ReacaoAgrupada[] {
  const porEmoji = new Map<string, { total: number; euReagi: boolean; quem: string[] }>()
  for (const reacao of reacoes) {
    const atual = porEmoji.get(reacao.emoji) ?? { total: 0, euReagi: false, quem: [] }
    atual.total += 1
    if (reacao.memberId === euMemberId) atual.euReagi = true
    atual.quem.push(reacao.nome)
    porEmoji.set(reacao.emoji, atual)
  }
  return [...porEmoji.entries()]
    .map(([emoji, dados]) => ({ emoji, ...dados }))
    .sort((a, b) => b.total - a.total)
}

/**
 * O nome que a conversa direta mostra.
 *
 * Um canal `pessoa` guarda os dois participantes, mas cada lado precisa ver o
 * NOME DO OUTRO — não o próprio, e não os dois juntos.
 */
export function nomeVisivelDoCanal(
  canal: Pick<CanalDeChat, 'kind' | 'name'>,
  membros: readonly Pick<MembroDoCanal, 'memberId' | 'name'>[],
  euMemberId: string,
): string {
  if (canal.kind !== 'pessoa') return canal.name
  const outro = membros.find((m) => m.memberId !== euMemberId)
  return outro?.name ?? canal.name
}

/* ------------------------------------------------ digitando --- */

/** Quem recebe apaga o aviso depois disto sem um novo quadro. */
export const DIGITANDO_EXPIRA_MS = 5_000
/** Quem digita reavisa no máximo a cada tanto — um quadro por tecla seria ruído. */
export const DIGITANDO_REENVIO_MS = 3_000

/**
 * A frase do topo da conversa.
 *
 * Em conversa direta o nome já está no cabeçalho, então basta "digitando…";
 * em grupo vai o nome, e com muita gente resume para não estourar a linha.
 */
export function textoDigitando(nomes: readonly string[], conversaDireta: boolean): string | null {
  if (nomes.length === 0) return null
  if (conversaDireta) return 'digitando…'
  const primeiro = (nome: string) => nome.split(' ')[0] ?? nome
  if (nomes.length === 1) return `${primeiro(nomes[0]!)} está digitando…`
  if (nomes.length === 2) return `${primeiro(nomes[0]!)} e ${primeiro(nomes[1]!)} estão digitando…`
  return `${primeiro(nomes[0]!)} e mais ${nomes.length - 1} estão digitando…`
}

/* ---------------------------------- contatos de fora do workspace --- */

/**
 * Toggle que liga buscar e convitar pessoas de OUTRAS empresas.
 *
 * Em avaliação (2026-09-13): o dono do produto ainda decide se fica. Quem é do
 * mesmo workspace conversa sem convite, com o toggle ligado ou não.
 */
export const TOGGLE_CONTATOS_EXTERNOS = 'CHAT_EXTERNAL_CONTACTS'

/** Busca em todo o Orbind só a partir disto: sem mínimo, dava para listar a base inteira. */
export const BUSCA_MINIMA_DE_PESSOAS = 3

/** Onde eu estou em relação a uma pessoa de fora. */
export type SituacaoDoContato = 'nenhuma' | 'convite_enviado' | 'convite_recebido' | 'contato'

/** Pessoa de fora — só nome e foto. E-mail não viaja: seria catálogo de outra empresa. */
export type PessoaDoOrbind = {
  memberId: string
  name: string
  avatarUrl: string | null
  hasAvatar: boolean
  avatarVersion: string | null
  situacao: SituacaoDoContato
  /** O convite pendente entre nós, quando houver. */
  conviteId: string | null
}

export type ConviteDeContato = {
  id: string
  direcao: 'recebido' | 'enviado'
  pessoa: Omit<PessoaDoOrbind, 'situacao' | 'conviteId'>
  enviadoEm: string
}

/**
 * Situação a partir da linha viva do par.
 *
 * Pura para a API e os testes concordarem: quem convidou vê "enviado", quem
 * recebeu vê "recebido" — a mesma linha, dois lados.
 */
export function situacaoDoContato(
  vinculo: { status: string; requesterMemberId: string } | null,
  euMemberId: string,
): SituacaoDoContato {
  if (!vinculo) return 'nenhuma'
  if (vinculo.status === 'accepted') return 'contato'
  if (vinculo.status !== 'pending') return 'nenhuma'
  return vinculo.requesterMemberId === euMemberId ? 'convite_enviado' : 'convite_recebido'
}

/* ------------------------------------------------------ menções --- */

/**
 * Parte o texto em pedaços comuns e menções `@Nome` de quem participa.
 *
 * O nome inteiro precisa bater — "@Ana" não destaca "Ana Martins" se só o
 * primeiro nome foi escrito, e isso é de propósito: é o nome que o
 * autocompletar insere. Nomes maiores são testados antes para "@Ana Maria" não
 * virar "@Ana" + " Maria".
 */
export function partesComMencao(
  corpo: string,
  nomes: readonly string[],
): { texto: string; mencao: boolean }[] {
  const validos = [...new Set(nomes.filter((n) => n.trim() !== ''))].sort((a, b) => b.length - a.length)
  if (validos.length === 0 || !corpo.includes('@')) return [{ texto: corpo, mencao: false }]
  const escapar = (t: string) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const padrao = new RegExp(`@(${validos.map(escapar).join('|')})`, 'g')
  const partes: { texto: string; mencao: boolean }[] = []
  let ultimo = 0
  for (const achado of corpo.matchAll(padrao)) {
    const inicio = achado.index ?? 0
    if (inicio > ultimo) partes.push({ texto: corpo.slice(ultimo, inicio), mencao: false })
    partes.push({ texto: achado[0], mencao: true })
    ultimo = inicio + achado[0].length
  }
  if (ultimo < corpo.length) partes.push({ texto: corpo.slice(ultimo), mencao: false })
  return partes
}

/* -------------------------------------------------------- links --- */

/** Pedaço do texto de uma mensagem: comum (`href` nulo) ou link clicável. */
export type ParteComLink = { texto: string; href: string | null }

/*
 * O que pode compor um endereço: letras e números de qualquer língua (caminho
 * com acento, domínio internacional) e a pontuação que a RFC 3986 permite.
 * Emoji, espaço, aspas e `<>` encerram o link — "veja x.com🎉" não leva o
 * emoji junto. O lookbehind evita achar "www." no meio de outra palavra
 * ("ana@www.x", "abc.www.x").
 */
const LINK_NO_TEXTO = /(?<![\p{L}\p{N}@._/-])(?:https?:\/\/|www\.)[\p{L}\p{N}\p{M}._~:/?#[\]@!$&'()*+,;=%-]+/giu

/** Pontuação que, no fim, é da frase e não do endereço ("veja x.com/a."). */
const PONTUACAO_DA_FRASE = new Set(['.', ',', ';', ':', '!', '?', "'", '*'])
const ABRE_DO_FECHA: Record<string, string> = { ')': '(', ']': '[' }

const contar = (texto: string, caractere: string) => texto.split(caractere).length - 1

/**
 * Tira do fim o que é da frase. Parêntese de fechamento só sai se estiver
 * sobrando: "(veja https://x.com)" perde o ")", mas
 * "https://pt.wikipedia.org/wiki/Java_(linguagem)" continua inteiro.
 */
function apararLink(bruto: string): string {
  let link = bruto
  for (;;) {
    const ultimo = link.at(-1)
    if (ultimo === undefined) return link
    const abre = ABRE_DO_FECHA[ultimo]
    const sobrando = abre !== undefined && contar(link, abre) < contar(link, ultimo)
    if (!PONTUACAO_DA_FRASE.has(ultimo) && !sobrando) return link
    link = link.slice(0, -1)
  }
}

/**
 * Endereço que o clique pode abrir, ou `null` se o trecho não é link de
 * verdade. Só http/https — nunca `javascript:`, `file:` e afins, mesmo que o
 * padrão acima já não os ache: o `href` vai direto para o navegador.
 */
function hrefDoLink(link: string): string | null {
  const href = /^www\./i.test(link) ? `https://${link}` : link
  let url: URL
  try {
    url = new URL(href)
  } catch {
    return null
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
  // host com ponto ("https://oi" é mais provável conversa que endereço);
  // localhost é a exceção útil em dev
  const host = url.hostname
  if (!host || host.endsWith('.') || (!host.includes('.') && host !== 'localhost')) return null
  return href
}

/**
 * Parte o texto em pedaços comuns e links clicáveis.
 *
 * Acha `http://`, `https://` e `www.` (este vira https). A pontuação colada no
 * fim fica fora do link. Pura e sem HTML: quem desenha monta os elementos, o
 * texto do usuário nunca vira marcação.
 */
export function partesComLinks(texto: string): ParteComLink[] {
  if (!/https?:\/\/|www\./i.test(texto)) return [{ texto, href: null }]
  const partes: ParteComLink[] = []
  let ultimo = 0
  for (const achado of texto.matchAll(LINK_NO_TEXTO)) {
    const inicio = achado.index ?? 0
    const link = apararLink(achado[0])
    const href = hrefDoLink(link)
    // trecho que não é link de verdade continua no texto comum
    if (href === null) continue
    if (inicio > ultimo) partes.push({ texto: texto.slice(ultimo, inicio), href: null })
    partes.push({ texto: link, href })
    ultimo = inicio + link.length
  }
  if (ultimo < texto.length) partes.push({ texto: texto.slice(ultimo), href: null })
  return partes.length > 0 ? partes : [{ texto, href: null }]
}
